/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { signUp_DLXAsync } from '../../actions/DataActionExtension.g';
import { INewsletterBlockViewProps } from './newsletter-block';
import { customExternalAPI as customeExternalAPIType } from './newsletter-block.props.autogenerated';

export interface ExternalPayload {
    email: string;
    marketing: boolean;
    profiling: boolean;
    thirdparty: boolean;
    message?: string;
}

const NewsletterBlockView: React.FC<INewsletterBlockViewProps> = props => {
    const euHostname = 'eu.venchi.com';
    const localeString = props?.context?.request?.locale ? props?.context?.request?.locale?.toLowerCase() : '';
    const sitePath = props?.context?.request?.sitePath ? props?.context?.request?.sitePath?.substring(1) : '';
    // const currency = props?.context?.request?.channel?.Currency;
    // const locale = sitePath !== '' ? sitePath : currency === 'EUR' && localeString === 'en-gb' ? 'eu' : localeString;
    const hostname = props?.context?.request?.url?.requestUrl?.hostname || '';
    let regionCRM: string;
    if (hostname.toLowerCase() === euHostname && sitePath === '') {
        regionCRM = 'eu';
    } else {
        regionCRM = localeString;
    }
    type Inputs = {
        email: string;
        marketing: boolean;
        profiling: boolean;
        thirdParts: boolean;
    };
    // const { register, handleSubmit, watch, errors } = useForm<Inputs>();
    const { register, handleSubmit, errors } = useForm<Inputs>();
    // const marketingChoice: boolean = watch('marketing');
    const [loading, setLoading] = React.useState(false);
    const [disabled_state, setDisabled] = React.useState(false);

    const customExternalAPI = props?.config?.customExternalAPI;
    const externalApiURL = props.config.externalApiURL ?? '';
    const externalThankYouMessage = props.config.externalThankYouMessage ?? '';
    const useExternalAPI = (externalApiURL && customExternalAPI === customeExternalAPIType.External) ?? false;

    let message = props.resources.verifyEmailText;
    // const channelId: number = props.context.request.apiSettings.channelId;
    const [subscribeMessage, setSubscribeMessage] = React.useState({
        state: false,
        message: '',
        class: ''
    });

    let source: string;
    switch (props.config.source?.toString()) {
        case 'CommerceSubscriberEU':
            source = 'Commerce Subscriber EU';
            break;
        case 'CommerceSubscribersUS':
            source = 'Commerce Subscribers US';
            break;
        case 'RetailEU':
            source = 'Retail EU';
            break;
        case 'RetailUS':
            source = 'Retail US';
            break;
    }

    const _subscribeToNL = async (data: Inputs) => {
        setDisabled(true);
        setLoading(true);
        signUp_DLXAsync(
            { callerContext: props.context?.actionContext },
            '',
            '',
            data.email,
            '',
            false,
            data.marketing,
            localeString !== 'en-us' ? data.profiling : data.marketing,
            data.thirdParts,
            localeString,
            '',
            '',
            source,
            regionCRM
        )
            .then(result => {
                message = result.Error
                    ? result.Error === 'already_subscribed'
                        ? props.resources.alreadySubscribedText
                        : result.Error
                    : message;
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: message,
                    class: 'success'
                });
                // @ts-ignore
                window.dataLayer?.push?.({
                    event: 'gaEvent',
                    eventCategory: 'newsletter',
                    eventAction: 'subscription',
                    //@ts-ignore
                    eventLabel: props.config.position
                });
            })
            .catch(error => {
                message = error.message;
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: error.message,
                    class: 'error'
                });
            });
    };

    function postJson(url: string, data: any): Promise<number> {
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.status;
            })
            .catch(error => {
                console.error('Error:', error);
                throw error;
            });
    }

    const _externalSubscribeToNL = async (data: Inputs) => {
        setLoading(true);
        const payload: ExternalPayload = {
            email: data.email,
            marketing: data.marketing,
            profiling: data.profiling,
            thirdparty: data.thirdParts
        };
        postJson(externalApiURL, payload)
            .then(result => {
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: externalThankYouMessage,
                    class: 'success'
                });
            })
            .catch(error => {
                message = error.message;
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: error.message,
                    class: 'error'
                });
            });
    };

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            {props.paragraph}
            {subscribeMessage.state === false ? (
                <form onSubmit={useExternalAPI ? handleSubmit(_externalSubscribeToNL) : handleSubmit(_subscribeToNL)}>
                    <div className={'email_row row'}>
                        <div aria-invalid={errors.email ? 'true' : 'false'}>
                            <input
                                name='email'
                                type='email'
                                ref={register({ required: true })}
                                placeholder={props.resources.yourEmailAddressText}
                                maxLength={320}
                            />
                            <button type='submit' disabled={disabled_state} className={`status__${subscribeMessage.class}`}>
                                {loading ? <div className={'loading'} /> : <span>{props.resources.subscribeBtnText}</span>}
                            </button>
                        </div>
                    </div>
                    <div className={'privacy_row row'}>
                        <div className={'inputBox privacy_field_ctn'} aria-invalid={errors.marketing ? 'true' : 'false'}>
                            <label>
                                <input
                                    ref={register({ required: true })}
                                    type={'checkbox'}
                                    name={'marketing'}
                                    defaultChecked={props.config.preflagMarketing ?? false}
                                    hidden={props.config.hideMarketing ?? false}
                                />
                                {props.privacyTextM}
                                {errors.marketing && (
                                    <p className='msc-alert msc-alert-error' role='alert'>
                                        {props.config.requiredMarketingError ?? 'This checkbox is required'}
                                    </p>
                                )}
                            </label>
                        </div>
                    </div>
                    <div className={`privacy_row row row--PrivacyP ${props.config.hideProfiling && 'hidden'}`}>
                        <div className={'inputBox privacy_field_ctn'}>
                            <label>
                                <input
                                    ref={register()}
                                    type={'checkbox'}
                                    name={'profiling'}
                                    defaultChecked={props.config.preflagProfiling ?? false}
                                />
                                {props.privacyTextP}
                            </label>
                        </div>
                    </div>
                    <div className={`privacy_row row row--PrivacyT ${props.config.hideThirdParty && 'hidden'}`}>
                        <div className={'inputBox privacy_field_ctn'}>
                            <label>
                                <input
                                    ref={register()}
                                    type={'checkbox'}
                                    name={'thirdParts'}
                                    defaultChecked={props.config.preflagThirdParty ?? false}
                                />
                                {props.privacyTextT}
                            </label>
                        </div>
                    </div>
                </form>
            ) : (
                <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                    {subscribeMessage.message}
                </div>
            )}
        </div>
    );
};

export default NewsletterBlockView;
